import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChatCircleDots, Minus, X, UsersThree, PaperPlaneRight, CaretUp } from "@phosphor-icons/react";
import { communityApi, type ChatSummary } from "@/api/client";
import { CHAT_OPEN_EVENT, chatKey, type ChatRef } from "@/lib/chatDock.core";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";

/****************
 * CHAT BOXES DOCKED AT THE BOTTOM OF EVERY PAGE. 2026-10-24.
 *
 * Guillaume: "private messages out of the feed; 2009-Facebook chat boxes docked
 * at the bottom (close x, minimise -), group chats, on every page." The
 * "Messages" tab lists the conversations with their unread counts; each opened
 * conversation is a box beside it (three at most, the oldest closes first).
 * Members only, as private messages (rest/community_chat.go): a non-member sees
 * nothing. An open box reads every 4 seconds; the dock's counts every 30.
 ****************/

const MAX_BOXES = 3;
const BOXES_KEY = "copyfi.chat.boxes";

function loadBoxes(): (ChatRef & { min?: boolean })[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(BOXES_KEY) || "[]");
    return Array.isArray(v) ? v.slice(0, MAX_BOXES) : [];
  } catch {
    return [];
  }
}

export function ChatDock() {
  const { t } = useTranslation("community");
  const viewOnly = isViewingAsSomeoneElse();
  const list = useQuery({ queryKey: ["community", "chat"], queryFn: communityApi.chatList, refetchInterval: 30_000, enabled: !viewOnly });
  const [boxes, setBoxes] = useState(loadBoxes);
  const [listOpen, setListOpen] = useState(false);
  useEffect(() => {
    try { sessionStorage.setItem(BOXES_KEY, JSON.stringify(boxes)); } catch { /* boxes reset on reload */ }
  }, [boxes]);
  useEffect(() => {
    const onOpen = (e: Event) => {
      const ref = (e as CustomEvent<ChatRef>).detail;
      if (!ref) return;
      setBoxes((bs) => {
        const rest = bs.filter((b) => chatKey(b) !== chatKey(ref));
        return [{ ...ref, min: false }, ...rest].slice(0, MAX_BOXES);
      });
    };
    window.addEventListener(CHAT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, onOpen);
  }, []);
  if (viewOnly || !list.data?.member) return null;
  const chats = list.data.chats;
  const unread = chats.reduce((n, c) => n + c.unread, 0);
  const close = (k: string) => setBoxes((bs) => bs.filter((b) => chatKey(b) !== k));
  const toggleMin = (k: string) => setBoxes((bs) => bs.map((b) => (chatKey(b) === k ? { ...b, min: !b.min } : b)));
  const open = (c: ChatSummary) => {
    window.dispatchEvent(new CustomEvent<ChatRef>(CHAT_OPEN_EVENT, {
      detail: c.kind === "group" ? { kind: "group", group: c.group, name: c.name } : { kind: "dm", handle: c.handle, name: c.name },
    }));
    setListOpen(false);
  };
  return (
    <div className="fixed bottom-0 end-24 z-40 hidden md:flex flex-row-reverse items-end gap-2 pointer-events-none" data-testid="chat-dock">
      <div className="pointer-events-auto w-64">
        {listOpen && <ChatList chats={chats} onOpen={open} />}
        <button type="button" onClick={() => setListOpen((v) => !v)}
          className="w-full flex items-center gap-2 rounded-t-lg border border-b-0 border-border/60 bg-card px-3 py-2 text-sm font-medium shadow-lg">
          <ChatCircleDots size={16} className="text-primary" />
          <span className="flex-1 text-start">{t("chat.title")}</span>
          {unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">{unread}</span>}
          <CaretUp size={12} className={listOpen ? "rotate-180" : ""} />
        </button>
      </div>
      {boxes.map((b) => (
        <ChatBox key={chatKey(b)} chat={b} minimised={!!b.min}
          unread={chats.find((c) => (b.kind === "group" ? c.group === b.group : c.handle === b.handle))?.unread ?? 0}
          onMin={() => toggleMin(chatKey(b))} onClose={() => close(chatKey(b))} />
      ))}
    </div>
  );
}

function ChatList({ chats, onOpen }: { chats: ChatSummary[]; onOpen: (c: ChatSummary) => void }) {
  const { t } = useTranslation("community");
  const qc = useQueryClient();
  const [making, setMaking] = useState(false);
  const [name, setName] = useState("");
  const [handles, setHandles] = useState("");
  const create = useMutation({
    mutationFn: () => communityApi.createGroup(name.trim(), handles.split(/[\s,]+/).map((h) => h.replace(/^@/, "")).filter(Boolean)),
    onSuccess: (g) => {
      setMaking(false); setName(""); setHandles("");
      void qc.invalidateQueries({ queryKey: ["community", "chat"] });
      onOpen({ kind: "group", group: g.id, name: g.name, last: "", unread: 0 });
    },
  });
  return (
    <div className="max-h-80 overflow-y-auto rounded-t-lg border border-b-0 border-border/60 bg-card shadow-lg">
      {chats.length === 0 && <p className="p-3 text-xs text-muted-foreground">{t("chat.empty")}</p>}
      {chats.map((c) => (
        <button key={c.kind === "group" ? `g${c.group}` : c.handle} type="button" onClick={() => onOpen(c)}
          className="w-full flex items-center gap-2 px-3 py-2 text-start text-sm hover:bg-white/[0.04]">
          {c.kind === "group" ? <UsersThree size={14} className="text-muted-foreground" /> : <ChatCircleDots size={14} className="text-muted-foreground" />}
          <span className={`flex-1 truncate ${c.unread ? "font-semibold" : ""}`}>{c.name}</span>
          {c.unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">{c.unread}</span>}
        </button>
      ))}
      <div className="border-t border-border/60 p-2">
        {making ? (
          <div className="space-y-1.5">
            <input className="w-full rounded border border-border/60 bg-transparent px-2 py-1 text-xs" placeholder={t("chat.groupName")} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            <input className="w-full rounded border border-border/60 bg-transparent px-2 py-1 text-xs" placeholder={t("chat.groupHandles")} value={handles} onChange={(e) => setHandles(e.target.value)} />
            {create.isError && <p className="text-[11px] text-danger">{(create.error as Error).message}</p>}
            <div className="flex gap-2">
              <button type="button" className="text-xs text-primary disabled:opacity-50" disabled={!name.trim() || !handles.trim() || create.isPending} onClick={() => create.mutate()}>{t("chat.create")}</button>
              <button type="button" className="text-xs text-muted-foreground" onClick={() => setMaking(false)}>{t("chat.cancel")}</button>
            </div>
          </div>
        ) : (
          <button type="button" className="text-xs text-primary" onClick={() => setMaking(true)}>{t("chat.newGroup")}</button>
        )}
      </div>
    </div>
  );
}

function ChatBox({ chat, minimised, unread, onMin, onClose }: { chat: ChatRef; minimised: boolean; unread: number; onMin: () => void; onClose: () => void }) {
  const { t } = useTranslation("community");
  const qc = useQueryClient();
  const isGroup = chat.kind === "group";
  const key = ["community", "chatbox", chatKey(chat)];
  const thread = useQuery({
    queryKey: key,
    queryFn: async () => {
      if (isGroup) {
        const g = await communityApi.groupThread(chat.group as number);
        return { messages: g.messages, members: g.members };
      }
      const d = await communityApi.dmThread(chat.handle as string);
      return { messages: d.messages.map((m) => ({ ...m, author: m.mine ? "" : chat.handle || "" })), members: [] as string[] };
    },
    enabled: !minimised,
    refetchInterval: minimised ? false : 4_000,
  });
  const msgs = useMemo(() => thread.data?.messages ?? [], [thread.data]);
  const newest = msgs.length ? msgs[msgs.length - 1].id : 0;
  // A DM's read mark moves when the box shows a newer message (a group's moves on read).
  const marked = useRef(0);
  useEffect(() => {
    if (isGroup || minimised || !newest || newest <= marked.current) return;
    marked.current = newest;
    void communityApi.dmRead(chat.handle as string, newest).then(() => qc.invalidateQueries({ queryKey: ["community", "chat"] }));
  }, [isGroup, minimised, newest, chat.handle, qc]);
  const end = useRef<HTMLDivElement>(null);
  // The thread's own box to its end, never scrollIntoView: that also moved the page's
  // overflow-hidden column, so pages opened in the middle (11-02).
  useEffect(() => { const box = end.current?.parentElement; if (box) box.scrollTop = box.scrollHeight; }, [newest, minimised]);
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: (body: string) => (isGroup ? communityApi.sendGroup(chat.group as number, body) : communityApi.sendDM(chat.handle as string, body)),
    onSuccess: () => { setText(""); void qc.invalidateQueries({ queryKey: key }); },
  });
  const leave = useMutation({
    mutationFn: () => communityApi.leaveGroup(chat.group as number),
    onSuccess: () => { onClose(); void qc.invalidateQueries({ queryKey: ["community", "chat"] }); },
  });
  return (
    <div className="pointer-events-auto w-72 rounded-t-lg border border-b-0 border-border/60 bg-card shadow-xl flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60">
        <button type="button" className="flex-1 truncate text-start text-sm font-medium" onClick={onMin}>
          {chat.name}{minimised && unread > 0 ? ` (${unread})` : ""}
        </button>
        <button type="button" aria-label={t("chat.minimise")} title={t("chat.minimise")} onClick={onMin} className="text-muted-foreground hover:text-foreground"><Minus size={14} /></button>
        <button type="button" aria-label={t("chat.close")} title={t("chat.close")} onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
      </div>
      {!minimised && (
        <>
          {isGroup && (thread.data?.members?.length ?? 0) > 0 && (
            <p className="px-3 pt-1.5 text-[11px] text-muted-foreground truncate">
              {thread.data!.members.map((h) => `@${h}`).join(", ")} · <button type="button" className="underline" onClick={() => leave.mutate()}>{t("chat.leave")}</button>
            </p>
          )}
          <div className="h-72 overflow-y-auto px-3 py-2 space-y-1.5">
            {thread.isError && <p className="text-xs text-danger">{(thread.error as Error).message}</p>}
            {msgs.map((m) => (
              <div key={m.id} className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
                {isGroup && !m.mine && m.author && <span className="text-[10px] text-muted-foreground">@{m.author}</span>}
                <span className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[13px] whitespace-pre-wrap break-words ${m.mine ? "bg-primary/20" : "bg-white/[0.06]"}`}>{m.body}</span>
                {m.warning && <span className="max-w-[85%] text-[11px] text-warning mt-0.5">{m.warning}</span>}
              </div>
            ))}
            <div ref={end} />
          </div>
          <form className="flex items-center gap-1.5 border-t border-border/60 p-2"
            onSubmit={(e) => { e.preventDefault(); if (text.trim()) send.mutate(text.trim()); }}>
            <input className="flex-1 rounded border border-border/60 bg-transparent px-2 py-1 text-[13px]" value={text} maxLength={2000}
              placeholder={t("messages.placeholder")} onChange={(e) => setText(e.target.value)} />
            <button type="submit" aria-label={t("messages.send")} disabled={!text.trim() || send.isPending} className="text-primary disabled:opacity-40"><PaperPlaneRight size={16} /></button>
          </form>
          {send.isError && <p className="px-2 pb-1 text-[11px] text-danger">{(send.error as Error).message || t("messages.sendFailed")}</p>}
        </>
      )}
    </div>
  );
}
